import type { FC, ReactNode } from "react";

import type { TypographyType } from "@aviary/components/Typography";
import type { Brand, Product, Variant } from "@shared/types/graphqlGenerated";

import { BrandName } from "../BrandName";
import { OnlyOnFullscriptTag } from "../OnlyOnFullscriptTag";
import { ProductFilledOnSiteTag } from "../ProductFilledOnSiteTag";
import { ProductName } from "../ProductName";
import { ProductSize } from "../ProductSize";
import { ProductUnitPricing } from "../ProductUnitPricing";

import { ProductInformationWrapper } from "./ProductInformationWrapper";

import * as styles from "./ProductInformation.styles";

type VariantType = Pick<Variant, "availabilityStatus" | "descriptor" | "msrp" | "sellPrice">;
type ProductType = Pick<Product, "name">;
type BrandType = Pick<Brand, "name">;

interface VariantSplinter extends VariantType {
  product: ProductType;
  brand: BrandType;
}

interface Props {
  changeOnHover?: boolean;
  isFilledOnSite?: boolean;
  isPillPack?: boolean;
  isNoClip?: boolean;
  isPromoActive?: boolean;
  productNameWordWrap?: boolean;
  showUnitPricing?: boolean;
  showVariant?: boolean;
  tags?: ReactNode;
  variant?: VariantSplinter;
  type?: TypographyType;
  children?: ReactNode;
}

const ProductInformation: FC<Props> = ({
  changeOnHover,
  children,
  isFilledOnSite,
  isPillPack,
  isNoClip,
  isPromoActive,
  productNameWordWrap,
  showUnitPricing,
  showVariant,
  tags,
  variant,
  type,
  ...rest
}) => {
  if (variant) {
    return (
      <ProductInformationWrapper {...rest}>
        <div css={styles.productTitle}>
          <ProductName
            type={type}
            isNoClip={isNoClip}
            changeOnHover={changeOnHover}
            wordWrap={productNameWordWrap}
            name={variant.product.name}
            data-e2e="product-name"
            css={styles.marginRight}
          />
          {isFilledOnSite && <ProductFilledOnSiteTag data-testid="filled-on-site" />}
          {isPillPack && <OnlyOnFullscriptTag />}
        </div>

        <BrandName name={variant.brand.name} isNoClip={isNoClip} />
        {tags}
        {showVariant && <ProductSize descriptor={variant.descriptor} />}
        {showUnitPricing && (
          <ProductUnitPricing
            isPromoActive={isPromoActive}
            sellPrice={variant.sellPrice}
            msrp={variant.msrp}
          />
        )}
        {children}
      </ProductInformationWrapper>
    );
  }
  return <ProductInformationWrapper {...rest}>{children}</ProductInformationWrapper>;
};

ProductInformation.defaultProps = {
  showUnitPricing: true,
  showVariant: true,
};

export { ProductInformation };
